<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>顧客ランキング</span>
      <div class="d-flex align-center">
        <v-select
          v-model="selectedType"
          :items="typeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 120px;"
          class="mr-2"
          @change="handleTypeChange"
        />
        <v-select
          v-model="selectedLimit"
          :items="limitOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 100px;"
          class="mr-2"
          @change="handleLimitChange"
        />
        <v-btn
          :disabled="isLoading || ranking.length === 0"
          color="secondary"
          small
          outlined
          @click="downloadCsv"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiDownload }}
          </v-icon>
          CSV
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <v-simple-table
        v-else-if="ranking.length > 0"
        dense
      >
        <template #default>
          <thead>
            <tr>
              <th
                class="text-center"
                style="width: 50px;"
              >
                順位
              </th>
              <th>顧客名</th>
              <th>指名キャスト</th>
              <th class="text-right">
                小計
              </th>
              <th class="text-right">
                大計
              </th>
              <th class="text-right">
                来店回数
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in ranking"
              :key="item.customerId"
              class="cursor-pointer"
              @click="handleRowClick(item)"
            >
              <td class="text-center">
                <v-chip
                  v-if="item.rank <= 3"
                  :color="getRankColor(item.rank)"
                  small
                  text-color="white"
                >
                  {{ item.rank }}
                </v-chip>
                <span v-else>{{ item.rank }}</span>
              </td>
              <td>{{ item.customerName }}</td>
              <td>{{ formatNominatedCasts(item.nominatedCasts) }}</td>
              <td class="text-right text-caption text--secondary">
                {{ formatCurrency(item.totalAmount) }}
              </td>
              <td class="text-right">
                {{ formatCurrency(item.totalAmountWithTax) }}
              </td>
              <td class="text-right text-caption text--secondary">
                {{ item.visitCount }}回
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div
        v-else
        class="text-center py-8 grey--text"
      >
        データがありません
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref, watch, computed,
} from '@vue/composition-api'
import { mdiDownload } from '@mdi/js'
import { formatCurrency } from '@/utils/formatters'
import { downloadCsv as downloadCsvFile } from '@/utils/csv'
import useCustomerRanking, { RANKING_TYPES, LIMIT_OPTIONS } from '../composables/useCustomerRanking'

// 日付を YYYY-MM-DD 文字列に変換（Date / 文字列どちらも対応）
const toDateStr = date => {
  if (!date) return ''
  if (typeof date === 'string') return date.slice(0, 10)

  return date.toISOString().slice(0, 10)
}

export default {
  name: 'CustomerRanking',
  props: {
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
  },
  setup(props, { root, emit }) {
    const {
      ranking,
      loading,
      fetchRanking,
    } = useCustomerRanking(root)

    const selectedType = ref('sales')
    const selectedLimit = ref(10)

    const isLoading = computed(() => loading.value)

    const typeOptions = RANKING_TYPES
    const limitOptions = LIMIT_OPTIONS

    const getRankColor = rank => {
      if (rank === 1) return '#D4A017'
      if (rank === 2) return '#7B8794'
      if (rank === 3) return '#A0522D'

      return 'grey'
    }

    // 指名キャストの表示整形
    // 指名金額が多い順（バックエンドでソート済み）の先頭1名を表示し、
    // 2人以上いる場合は「{名前} 他N名」と残り人数を表示する
    const formatNominatedCasts = casts => {
      if (!casts || casts.length === 0) return '-'

      const [first, ...rest] = casts
      if (rest.length === 0) return first.name

      return `${first.name} 他${rest.length}名`
    }

    const loadRanking = () => {
      fetchRanking({
        rankingType: selectedType.value,
        rankingLimit: selectedLimit.value,
        startDate: props.startDate,
        endDate: props.endDate,
      })
    }

    const handleTypeChange = () => {
      loadRanking()
    }

    const handleLimitChange = () => {
      loadRanking()
    }

    const handleRowClick = item => {
      emit('customer-click', item.customerId)
    }

    // 表示中のランキングをCSVとしてダウンロードする
    const downloadCsv = () => {
      if (ranking.value.length === 0) return

      const headers = ['順位', '顧客名', '指名キャスト', '小計', '大計', '来店回数']
      const rows = ranking.value.map(item => [
        item.rank,
        item.customerName,
        // CSVには指名キャスト全員分を「、」区切りで出力（金額多い順）
        (item.nominatedCasts || []).map(c => c.name).join('、'),
        item.totalAmount,
        item.totalAmountWithTax,
        item.visitCount,
      ])

      const typeLabel = (RANKING_TYPES.find(t => t.value === selectedType.value) || {}).text || selectedType.value
      const period = [toDateStr(props.startDate), toDateStr(props.endDate)].filter(Boolean).join('_')
      const fileName = `顧客ランキング_${typeLabel}${period ? `_${period}` : ''}.csv`

      downloadCsvFile({ headers, rows, fileName })
    }

    // 日付が変更されたらリロード
    watch(
      () => [props.startDate, props.endDate],
      () => {
        loadRanking()
      },
      { immediate: true },
    )

    return {
      ranking,
      isLoading,
      selectedType,
      selectedLimit,
      typeOptions,
      limitOptions,
      formatCurrency,
      getRankColor,
      formatNominatedCasts,
      handleTypeChange,
      handleLimitChange,
      handleRowClick,
      downloadCsv,
      icons: { mdiDownload },
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
